import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import QuestionBuilder, { type QuestionData } from '../components/QuestionBuilder'
import { Button, Card, CardHeader, Input, Textarea, Select, Alert, PageLayout, PageHeader } from '../components/ui'

interface Organization {
  id: string
  name: string
}

const SURVEY_TYPES = [
  'Impact Assessment',
  'Needs Assessment',
  'Program Feedback',
  'Pre/Post Evaluation',
  'Custom',
]

const SURVEY_TYPE_LABEL_IDS: Record<string, string> = {
  'Impact Assessment': 'createSurvey.surveyTypeImpactAssessment',
  'Needs Assessment': 'createSurvey.surveyTypeNeedsAssessment',
  'Program Feedback': 'createSurvey.surveyTypeProgramFeedback',
  'Pre/Post Evaluation': 'createSurvey.surveyTypePrePostEvaluation',
  'Custom': 'createSurvey.surveyTypeCustom',
}

const CreateSurveyPage: React.FC = () => {
  useAuth()
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [organizationId, setOrganizationId] = useState(searchParams.get('org') || '')
  const [orgsLoading, setOrgsLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [surveyType, setSurveyType] = useState('Impact Assessment')
  const [minDaysBetweenAttempts, setMinDaysBetweenAttempts] = useState(30)
  const [autoRemind, setAutoRemind] = useState(true)
  const [questions, setQuestions] = useState<QuestionData[]>([])

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch(API_ENDPOINTS.organizations.base, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        })
        if (response.ok) {
          const data = await response.json()
          setOrganizations(data)
          const orgFromUrl = searchParams.get('org')
          if (orgFromUrl && data.some((o: Organization) => o.id === orgFromUrl)) {
            setOrganizationId(orgFromUrl)
          } else if (data.length === 1) {
            setOrganizationId(data[0].id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err)
      } finally {
        setOrgsLoading(false)
      }
    }
    fetchOrganizations()
  }, [])

  const handleSubmit = async (publish: boolean) => {
    if (!organizationId) { setError(intl.formatMessage({ id: 'createSurvey.errorSelectOrganization' })); return }
    if (!title.trim()) { setError(intl.formatMessage({ id: 'createSurvey.errorTitleRequired' })); return }
    if (questions.length === 0) { setError(intl.formatMessage({ id: 'createSurvey.errorAddQuestion' })); return }
    if (questions.some(q => !q.text.trim())) { setError(intl.formatMessage({ id: 'createSurvey.errorQuestionsNeedText' })); return }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      const headers = { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }

      // Build questions payload with scoring_config
      const questionsPayload = questions.map((q, i) => ({
        text: q.text,
        type: q.type,
        required: q.required,
        order: i,
        options: q.options,
        scoring_config: q.scoring_config && q.scoring_config.type ? q.scoring_config : null,
      }))

      // Create survey with questions inline
      const surveyResponse = await fetch(API_ENDPOINTS.surveys.base, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title,
          description,
          organization_id: organizationId,
          settings: { survey_type: surveyType },
          min_days_between_attempts: minDaysBetweenAttempts,
          auto_remind: autoRemind,
          questions: questionsPayload,
        })
      })

      if (!surveyResponse.ok) throw new Error('Failed to create survey')
      const survey = await surveyResponse.json()

      // Publish if requested
      if (publish) {
        await fetch(API_ENDPOINTS.surveys.byId(survey.id), {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: 'PUBLISHED' })
        })
      }

      navigate(`/surveys/${survey.id}/share`)
    } catch (err) {
      console.error('Error creating survey:', err)
      setError(intl.formatMessage({ id: 'createSurvey.errorCreateFailed' }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageLayout maxWidth="md">
      <PageHeader
        title={intl.formatMessage({ id: 'createSurvey.pageTitle' })}
        subtitle={intl.formatMessage({ id: 'createSurvey.pageSubtitle' })}
        backTo="/dashboard"
        backLabel={intl.formatMessage({ id: 'createSurvey.backToDashboard' })}
      />

      {error && (
        <Alert variant="error" className="mb-6">{error}</Alert>
      )}

      {/* Survey Details */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'createSurvey.surveyDetails' })} />
        <div className="space-y-4">
          {/* Organization */}
          <div>
            {orgsLoading ? (
              <p className="text-sm text-foreground-muted"><FormattedMessage id="createSurvey.loading" /></p>
            ) : organizations.length === 0 ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  <FormattedMessage id="createSurvey.organization" /> <span className="text-error">*</span>
                </label>
                <p className="text-sm text-foreground-muted"><FormattedMessage id="createSurvey.needOrganization" /></p>
                <Link to="/organizations" className="text-sm text-primary hover:text-primary"><FormattedMessage id="createSurvey.createOne" /></Link>
              </div>
            ) : (
              <Select
                id="organization"
                label={intl.formatMessage({ id: 'createSurvey.organization' })}
                required
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                placeholder={intl.formatMessage({ id: 'createSurvey.selectOrganization' })}
                options={organizations.map((org) => ({ value: org.id, label: org.name }))}
              />
            )}
          </div>

          {/* Title */}
          <Input
            id="title"
            label={intl.formatMessage({ id: 'createSurvey.title' })}
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.formatMessage({ id: 'createSurvey.titlePlaceholder' })}
          />

          {/* Description */}
          <Textarea
            id="description"
            label={intl.formatMessage({ id: 'createSurvey.description' })}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={intl.formatMessage({ id: 'createSurvey.descriptionPlaceholder' })}
          />

          {/* Survey Type */}
          <Select
            id="surveyType"
            label={intl.formatMessage({ id: 'createSurvey.surveyType' })}
            value={surveyType}
            onChange={(e) => setSurveyType(e.target.value)}
            options={SURVEY_TYPES.map((t) => ({ value: t, label: intl.formatMessage({ id: SURVEY_TYPE_LABEL_IDS[t] }) }))}
          />
        </div>
      </Card>

      {/* Settings */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'createSurvey.settings' })} />
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground"><FormattedMessage id="createSurvey.daysBetweenRetakes" /></label>
            <input
              type="number"
              value={minDaysBetweenAttempts}
              onChange={(e) => setMinDaysBetweenAttempts(Math.max(0, parseInt(e.target.value) || 0))}
              min={0}
              max={365}
              className="w-20 px-2 py-1 rounded border border-border text-foreground bg-surface sm:text-sm"
            />
            <span className="text-xs text-foreground-muted"><FormattedMessage id="createSurvey.noLimitHint" /></span>
          </div>
          <label className="flex items-center">
            <input type="checkbox" checked={autoRemind}
              onChange={(e) => setAutoRemind(e.target.checked)}
              className="h-4 w-4 text-primary border-border rounded mr-2" />
            <span className="text-sm text-foreground"><FormattedMessage id="createSurvey.autoRemind" /></span>
          </label>
        </div>
      </Card>

      {/* Questions */}
      <Card className="mb-6">
        <CardHeader title={intl.formatMessage({ id: 'createSurvey.questionsCount' }, { count: questions.length })} />
        <QuestionBuilder questions={questions} onChange={setQuestions} />
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Link to="/dashboard">
          <Button variant="outline"><FormattedMessage id="createSurvey.cancel" /></Button>
        </Link>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => handleSubmit(false)} loading={loading}>
            {loading ? <FormattedMessage id="createSurvey.saving" /> : <FormattedMessage id="createSurvey.saveAsDraft" />}
          </Button>
          <Button variant="primary" onClick={() => handleSubmit(true)} loading={loading}>
            {loading ? <FormattedMessage id="createSurvey.publishing" /> : <FormattedMessage id="createSurvey.publishSurvey" />}
          </Button>
        </div>
      </div>
    </PageLayout>
  )
}

export default CreateSurveyPage
